import axios, { type AxiosRequestConfig } from "axios"

import { API_ROUTES } from "@/lib/api-routes"
import { queryClient } from "@/lib/query-client"
import { PORTAL_MODULES_KEY } from "@/lib/query-keys"

/**
 * Single axios instance for the whole app.
 *
 * AUTH MODEL — server-side sessions via an HttpOnly cookie
 * (`voxlane_session`), NOT JWT/bearer tokens:
 *   - The cookie is set/cleared by the backend and is invisible to JS.
 *     There is no token to read or store — never touch
 *     localStorage/sessionStorage for auth.
 *   - `withCredentials: true` makes the browser attach the cookie to
 *     every request (and honour Set-Cookie on responses). Without it the
 *     session cookie is not sent and every call 401s.
 *   - "Am I logged in?" is answered by calling `GET /auth/session`, not
 *     by inspecting a token.
 *
 * `baseURL` is the API origin (`VITE_API_URL`) + `/api/v1`. The backend
 * must allow this origin with `Access-Control-Allow-Credentials: true`.
 */
const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000"

export const axiosInstance = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
})

/**
 * File uploads must NOT inherit the JSON default above.
 *
 * Axios normally detects a `FormData` body and sets
 * `multipart/form-data; boundary=…` itself — but an explicit default
 * `Content-Type` wins, and axios then serialises the FormData to JSON, so
 * the file never leaves the browser (the server reports the field as
 * missing with `input: null`). Clearing the header lets the browser
 * generate the correct type and boundary.
 */
axiosInstance.interceptors.request.use((config) => {
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    config.headers.delete("Content-Type")
  }
  return config
})

/**
 * Absolute URL for an API path (axios `baseURL` + `path`). Use for
 * transports that bypass axios — notably `EventSource` (SSE), which needs
 * a full URL and sends the session cookie via `withCredentials`.
 *
 *   new EventSource(apiUrl(API_ROUTES.DEPLOYMENTS.EVENTS(id)), {
 *     withCredentials: true,
 *   })
 */
export function apiUrl(path: string): string {
  return `${axiosInstance.defaults.baseURL ?? ""}${path}`
}

// Endpoints whose 401s are expected and handled by the caller (the
// session probe when logged out, and a failed login attempt) — these
// must NOT trigger the global bounce-to-login.
const AUTH_PROBE_URLS: string[] = [
  API_ROUTES.AUTH.SESSION,
  API_ROUTES.AUTH.LOGIN,
  API_ROUTES.AUTH.LOGOUT,
]

// Global response handling: if a session expires mid-use, bounce to the
// login screen. Skipped for the auth-probe endpoints above and when
// already on /login (so there's no redirect loop).
/**
 * A file request (`responseType: "blob"`) gets its error body as a Blob
 * too, so `extractErrorMessage` would find no `detail` and fall back to
 * axios's "Request failed with status code 500". Read a JSON or text error
 * body back into an object first, so every export, PDF and recording
 * failure shows the server's own message. Runs before the auth handling
 * below (response interceptors run in the order they're added).
 */
axiosInstance.interceptors.response.use(undefined, async (error) => {
  const data = error?.response?.data
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    const type = data.type.toLowerCase()
    if (type.includes("json") || type.startsWith("text/plain")) {
      try {
        const text = await data.text()
        try {
          error.response.data = JSON.parse(text)
        } catch {
          error.response.data = { detail: text }
        }
      } catch {
        // Unreadable body — leave it; the caller's fallback message shows.
      }
    }
  }
  return Promise.reject(error)
})

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (typeof window === "undefined") return Promise.reject(error)

    const status = error.response?.status
    const url = error.config?.url as string | undefined
    const onLoginPage = window.location.pathname.startsWith("/login")
    const isAuthProbe = url ? AUTH_PROBE_URLS.includes(url) : false

    if (status === 401 && !onLoginPage && !isAuthProbe) {
      window.location.href = "/login"
    }

    /**
     * 403 = entitlement revoked. The backend re-checks module access on
     * every request, so this can appear mid-session on a page that worked
     * a minute ago. Drop the cached station list so the rail re-renders
     * against reality instead of showing a station that no longer exists.
     * The screen itself renders its own "no access" state.
     */
    if (status === 403) {
      void queryClient.invalidateQueries({ queryKey: PORTAL_MODULES_KEY })
    }

    return Promise.reject(error)
  }
)

/**
 * Thin generic CRUD wrapper around a single endpoint. Features call
 * `axiosInstance` directly inside their query hooks; this is a
 * convenience for simple resources and for the typed auth/session
 * helpers.
 *
 *   const users = new APIClient<User, UserCreate>(API_ROUTES.USERS.LIST)
 *   await users.getAll()
 */
class APIClient<T, U = T> {
  endpoint: string

  constructor(endpoint: string) {
    this.endpoint = endpoint
  }

  getAll = async (config: AxiosRequestConfig = {}) => {
    const res = await axiosInstance.get<T[]>(this.endpoint, config)
    return res.data
  }

  get = async (config: AxiosRequestConfig = {}) => {
    const res = await axiosInstance.get<T>(this.endpoint, config)
    return res.data
  }

  post = async (data: U, config: AxiosRequestConfig = {}) => {
    const res = await axiosInstance.post<T>(this.endpoint, data, config)
    return res.data
  }

  put = async (data: T, config: AxiosRequestConfig = {}) => {
    const res = await axiosInstance.put<T>(this.endpoint, data, config)
    return res.data
  }

  patch = async <P = Partial<T>>(data: P, config: AxiosRequestConfig = {}) => {
    const res = await axiosInstance.patch<T>(this.endpoint, data, config)
    return res.data
  }

  delete = async (config: AxiosRequestConfig = {}) => {
    const res = await axiosInstance.delete<T>(this.endpoint, config)
    return res.data
  }
}

export default APIClient
